// Shared helpers for the cabinet's pages.
import { formatLatex, renderMath } from '../markdown.js';
import { loadFull, loadCompetitions, compNameSync } from '../data.js';
import { pluralUk } from '../util/plural.js';
import { icon } from '../util/icons.js';

export const esc = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
export const md = (s) => formatLatex(String(s ?? ''));
export { pluralUk, loadFull, compNameSync };

// The archive bank of year one (tools/cabinet/build_bank.py): concept items for
// the placement test and ~140 candidate problems per topic for personal plans.
let year1 = null;
export async function curriculum() {
    if (year1) return year1;
    year1 = await (await fetch('/cabinet/year1.json')).json();
    competitions();
    return year1;
}
// The competitions' names (compLine): one request for every caller, never rejects.
let comps = null;
export const competitions = () => (comps ||= loadCompetitions().catch(() => { comps = null; return null; }));
export const unitById = (id) => year1?.units.find(u => u.id === id);
export const bankItem = (slug) => {
    for (const list of Object.values(year1?.bank || {})) { const p = list.find(x => x.s === slug); if (p) return p; }
    return null;
};

export const bankFor = (code) => year1?.bank?.[code] || [];
export const SUBJECT = { 'teoriia-chysel': 'Теорія чисел', kombinatoryka: 'Комбінаторика', alhebra: 'Алгебра', heometriia: 'Геометрія' };
export const STATUS = {
    learn: { label: 'Треба пройти', glyph: '○' },
    basics: { label: 'Основи є', glyph: '◐' },
    mastered: { label: 'Знаю', glyph: '●' },
};
export const TASK = {
    theory: 'Теорія', example: 'Розбір', lesson: 'Класна', home: 'Вдома', extra: 'Складніша',
    review: 'Повторення', deep: 'Поглиблення', assessment: 'Зріз', game: 'Практикум',
};

// ── Navigation: real paths under /cabinet, no reloads ─────────────────────
export function navigate(path, { replace = false } = {}) {
    if (replace) history.replaceState(null, '', path); else history.pushState(null, '', path);
    window.dispatchEvent(new Event('cabinet:route'));
}
document.addEventListener('click', (e) => {
    const a = e.target.closest('a[href^="/cabinet"]');
    if (!a || a.target === '_blank' || e.metaKey || e.ctrlKey || e.shiftKey || e.button) return;
    const href = a.getAttribute('href');
    if (/\.(json|sql|md|html)$/.test(href)) return;
    e.preventDefault();
    navigate(href);
});
// A file of the site's own (a lesson PDF) is fetched by the page and saved from
// a blob. Safari sends an <a download> without the staging password and the
// 401 ends the download silently (2026-10-11); the page's fetch carries it.
// Signed bucket links are another origin and stay plain links.
document.addEventListener('click', async (e) => {
    const a = e.target.closest('a[download]');
    if (!a || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button) return;
    const url = new URL(a.href, location.href);
    if (url.origin !== location.origin || !/^https?:$/.test(url.protocol)) return;   // blob: is same-origin too
    e.preventDefault();
    try {
        const r = await fetch(url);
        if (!r.ok) throw new Error(String(r.status));
        const blob = URL.createObjectURL(await r.blob());
        const t = Object.assign(document.createElement('a'), { href: blob, download: a.getAttribute('download') || decodeURIComponent(url.pathname.split('/').pop()) });
        document.body.append(t); t.click(); t.remove();
        setTimeout(() => URL.revokeObjectURL(blob), 60000);
    } catch { location.assign(url.href); }
});

export function mount(html) {
    const app = document.getElementById('app');
    app.innerHTML = html;
    app.focus({ preventScroll: true });
    window.scrollTo(0, 0);
    renderMath(app).catch(() => {});
    // Deep links to a step of a page (#s-hw, #s-att …) land on that step.
    if (location.hash.length > 1) setTimeout(() => document.getElementById(decodeURIComponent(location.hash.slice(1)))?.scrollIntoView({ block: 'start' }), 60);
    return app;
}
// Where a page sits: the whole way up the tree, one link per level, then the
// page itself (unlinked) when its heading does not already say it. In the page,
// above its heading, never in the top bar (user 2026-10-09). Each step is
// [label, href]; a step without href is the current page; falsy steps drop out.
// The last linked step carries `is-up`, the one step a phone shows.
export function crumbs(...trail) {
    const steps = trail.filter(Boolean);
    if (!steps.length) return '';
    const up = steps.findLastIndex(([, href]) => href);
    return `<nav class="cab-crumbs" aria-label="Розташування"><ol>${steps.map(([label, href], i) => `<li${i === up ? ' class="is-up"' : ''}>${href
        ? `<a href="${esc(href)}" data-track="none">${esc(label)}</a>`
        : `<span aria-current="page">${esc(label)}</span>`}</li>`).join('')}</ol></nav>`;
}
// The live strip under the top bar shows a lesson that is starting or running;
// app.js clears it on every route.
export function setOnAir(html) {
    const el = document.getElementById('onAir');
    if (!el) return;
    el.innerHTML = html || '';
    el.hidden = !html;
}
export function typeset(el) { return renderMath(el).catch(() => {}); }
export function loading(text = 'Завантаження…') { mount(`<p class="boot">${esc(text)}</p>`); }
// The browser's own words for a dropped connection are English («Failed to
// fetch», «Load failed», «NetworkError…»): say it in Ukrainian, with what to do.
const OFFLINE = /failed to fetch|load failed|networkerror|network request failed|err_internet|timed? ?out|abort/i;
export function failure(err) {
    const msg = String(err?.message || err || '');
    const text = !navigator.onLine || OFFLINE.test(msg) ? 'Немає зв’язку з сервером. Перевір інтернет і онови сторінку.' : /[а-яіїєґ]/i.test(msg) ? msg : 'Сторінка не відкрилася. Онови її або повернись на головну.';
    mount(`<section class="cab-narrow"><h1 class="cab-h1">Щось пішло не так</h1><p class="st-read">${esc(text)}</p>
      <p><button type="button" class="btn btn-drawn" id="failReload" data-track="none">Оновити</button></p>
      <p><a class="st-link" href="/cabinet/" data-track="none">На головну кабінету</a></p></section>`).querySelector('#failReload').addEventListener('click', () => location.reload());
    console.error(err);
}

export function toast(text) {
    const box = document.getElementById('toastContainer');
    if (!box) return;
    const t = document.createElement('div');
    t.className = 'toast';
    t.textContent = text;
    box.appendChild(t);
    setTimeout(() => t.remove(), 3400);
}

export function fmtDate(ts, withTime = false) {
    const d = new Date(ts);
    return d.toLocaleDateString('uk-UA', { timeZone: 'Europe/Kyiv',  day: 'numeric', month: 'long' }) + (withTime ? ', ' + d.toLocaleTimeString('uk-UA', { timeZone: 'Europe/Kyiv',  hour: '2-digit', minute: '2-digit' }) : '');
}
export const fmtDay = (ts) => new Date(ts).toLocaleDateString('uk-UA', { timeZone: 'Europe/Kyiv',  weekday: 'short', day: 'numeric', month: 'short' });
export const minutesLabel = (m) => m >= 60 ? `${Math.floor(m / 60)} год${m % 60 ? ' ' + (m % 60) + ' хв' : ''}` : `${m} хв`;
// Grade band of a group: «8 кл.», «5–7 кл.»
export const grades = (g, unit = 'кл.') => !g?.grade_min ? '' : g.grade_min === g.grade_max || !g.grade_max ? `${g.grade_min} ${unit}` : `${g.grade_min}–${g.grade_max} ${unit}`;
// No nicknames for now (user 2026-10-09): the full name first, the generated
// nickname only when a profile has no name yet.
export const nick = (p) => p?.full_name || p?.preferred_name || '—';
export const initials = (p) => (p?.full_name || p?.preferred_name || '?').trim().slice(0, 1).toUpperCase();
// Uliana's avatar wears a small blue crown (user 2026-10-14), drawn by CSS
// (.is-crowned) only while the avatar holds a photo. Matched by the SHA-256 of
// her sign-in email, so the address itself is not in the public bundle.
const CROWNED = new Set(['15dbdffda272a9908708aaad91bd4ffa29cb98a80ce510c192f04c5a4c310e2a']);
const crownChecks = new Map();   // email → Promise<boolean>
export function crowned(p) {
    const email = String(p?.email || '').trim().toLowerCase();
    if (!email || !globalThis.crypto?.subtle) return Promise.resolve(false);
    if (!crownChecks.has(email)) crownChecks.set(email, crypto.subtle.digest('SHA-256', new TextEncoder().encode(email))
        .then(b => CROWNED.has(Array.from(new Uint8Array(b), x => x.toString(16).padStart(2, '0')).join('')))
        .catch(() => false));
    return crownChecks.get(email);
}

export function compLine(rec) {
    const name = compNameSync(rec.competition_code) || '';
    const year = rec.year ? ` · ${rec.year}` : '';
    return `${esc(name)}${year}`;
}

// Cards on phones, a table from 720px: the same rows, two layouts.
export function table(cols, rows, { empty = 'Немає даних.', cls = '' } = {}) {
    if (!rows.length) return `<p class="fine">${esc(empty)}</p>`;
    return `<div class="rtable ${cls}" role="table">
      <div class="rt-head" role="row">${cols.map(c => `<span role="columnheader" class="${c.cls || ''}">${esc(c.label)}</span>`).join('')}</div>
      ${rows.map(r => `<div class="rt-row ${r.__cls || ''}" role="row">${cols.map(c => `<span role="cell" class="${c.cls || ''}" data-label="${esc(c.label)}">${c.html ? c.html(r) : esc(c.get(r))}</span>`).join('')}</div>`).join('')}
    </div>`;
}

// Tiny inline bar for a share (0–1).
export const bar = (pct) => `<span class="bar"><span style="width:${Math.round(Math.max(0, Math.min(1, pct)) * 100)}%"></span></span>`;

// «Додай на головний екран»: most students use the cabinet on a phone. Shown on
// a phone, outside the installed app, until dismissed. Android offers a real
// install prompt; iOS needs Share → «На початковий екран».
let installEvt = null;
window.addEventListener('beforeinstallprompt', (e) => { e.preventDefault(); installEvt = e; });
export function installHint() {
    const standalone = window.matchMedia('(display-mode: standalone)').matches || navigator.standalone;
    let dismissed = false;
    try { dismissed = localStorage.getItem('cab-install-hint') === 'no' || localStorage.getItem('cab-guide') !== '1'; } catch {}
    if (standalone || dismissed || !window.matchMedia('(max-width: 719px)').matches) return '';
    const ios = /iPhone|iPad|iPod/.test(navigator.userAgent);
    return `<div class="card install" id="installHint"><p><b>Кабінет — як застосунок.</b> ${ios ? 'Натисни «Поділитися» ⎋ і «На початковий екран».' : 'Додай його на головний екран: відкриватиметься одним дотиком.'}</p>
      <div class="ans-actions">${ios ? '' : '<button type="button" class="btn btn-primary" id="installGo" data-track="none">Додати</button>'}<button type="button" class="btn btn-drawn" id="installNo" data-track="none">Не зараз</button></div></div>`;
}
export function wireInstallHint(root) {
    const box = root.querySelector('#installHint');
    if (!box) return;
    box.querySelector('#installNo').addEventListener('click', () => { try { localStorage.setItem('cab-install-hint', 'no'); } catch {} box.remove(); });
    box.querySelector('#installGo')?.addEventListener('click', async () => {
        if (!installEvt) { toast('У меню браузера: «Додати на головний екран»'); return; }
        installEvt.prompt(); await installEvt.userChoice.catch(() => {}); installEvt = null; box.remove();
    });
}

// A work's photos full screen (the grading pages): ← → or a sideways swipe walk the
// pages, a click on the photo zooms there (2.5×, then it pans); ✕, Esc or a click
// beside the photo closes. Unzoomed, nothing scrolls (styles: .gr-view-b).
export function viewPages(files, start) {
    let i = start;
    const d = document.createElement('dialog');
    d.className = 'ph-view gr-view';
    d.innerHTML = `<div class="gr-view-b"><img class="gr-view-img" draggable="false" src="${esc(files[start].url || '')}" alt="Сторінка ${start + 1}"></div>
      <p class="gr-view-n" aria-live="polite"></p>
      ${files.length > 1 ? `<button type="button" class="gr-view-go is-prev" aria-label="Попередня сторінка" data-track="none">${icon('chevron-left', { size: 22 })}</button>
      <button type="button" class="gr-view-go is-next" aria-label="Наступна сторінка" data-track="none">${icon('chevron-right', { size: 22 })}</button>` : ''}
      <button type="button" class="ph-close" aria-label="Закрити" data-track="none">${icon('close', { size: 20 })}</button>`;
    const img = d.querySelector('img'), box = d.querySelector('.gr-view-b');
    const go = (k) => {
        i = (k + files.length) % files.length;
        d.classList.remove('is-zoomed'); img.style.width = '';
        img.src = files[i].url || ''; img.alt = `Сторінка ${i + 1}`;
        d.setAttribute('aria-label', `Сторінка ${i + 1} з ${files.length}`);
        d.querySelector('.gr-view-n').textContent = `Сторінка ${i + 1} з ${files.length}`;
    };
    // A swipe (unzoomed): far enough sideways and more sideways than down turns the page;
    // the click that follows it is not a zoom.
    let x0 = null, y0 = 0, swiped = false;
    box.addEventListener('pointerdown', (e) => { if (!d.classList.contains('is-zoomed')) { x0 = e.clientX; y0 = e.clientY; swiped = false; } });
    box.addEventListener('pointerup', (e) => {
        if (x0 == null) return;
        const dx = e.clientX - x0, dy = e.clientY - y0;
        x0 = null;
        if (files.length > 1 && Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) { swiped = true; go(dx < 0 ? i + 1 : i - 1); }
    });
    box.addEventListener('pointercancel', () => { x0 = null; });
    img.addEventListener('click', (e) => {
        if (swiped) { swiped = false; return; }
        if (d.classList.toggle('is-zoomed')) {
            const r = img.getBoundingClientRect(), fx = (e.clientX - r.left) / r.width, fy = (e.clientY - r.top) / r.height;
            img.style.width = `${Math.round((r.width || box.clientWidth / 2) * 2.5)}px`;
            requestAnimationFrame(() => { box.scrollLeft = fx * img.offsetWidth - box.clientWidth / 2; box.scrollTop = fy * img.offsetHeight - box.clientHeight / 2; });
        } else img.style.width = '';
    });
    d.addEventListener('click', (e) => {
        if (swiped && e.target === box) { swiped = false; return; }
        if (e.target.closest('.ph-close') || e.target === box) d.close();
        else if (e.target.closest('.is-prev')) go(i - 1);
        else if (e.target.closest('.is-next')) go(i + 1);
    });
    d.addEventListener('keydown', (e) => { if (e.key === 'ArrowLeft') go(i - 1); else if (e.key === 'ArrowRight') go(i + 1); });
    d.addEventListener('close', () => d.remove());
    document.body.append(d);
    go(i);
    d.showModal();
}
